// TODO: Refactor this file to use ant's Form component
import React, { useState, useEffect, useCallback, useMemo, useContext, useReducer } from 'react';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import moment from 'moment';

import { ApplicationUIContext, currencySort, dateSort, basicSort } from '@yuhuinc/ui-library';
import { Radio } from 'antd';
import { featureEnabled } from 'helpers/ToggleBot';
import LeadPreferencesAlertInfo from './LeadPreferencesAlertInfo';
import AssignUnitTable from './AssignUnitTable';
import { SiteField, PropertyField, LeaseStartDateField, GroupField } from './AssignUnitFields';
import ActionFooter from '../common/ActionFooter';

import { getTranslatedString, getCurrentLocale } from 'helpers/i18n';
import { alphabeticalColumnSort } from 'helpers/tableHelpers';

import { getAvailableUnits } from '../store/actions';
import { showFailure } from 'actions/notificationActions';
import { hostIsCapreit } from 'helpers/CurrentWindowLocation';

const UNIT_ASSIGN_FORM = 'unit_assign_form';
const WAIT_UNIT_FORM = 'wait_unit_form';

const StyledContainer = styled.div`
  width: 100%;
  height: 100%;
  padding: 24px 24px 0px;
  overflow-x: ${(props) => props.isMobile && 'hidden'};
  overflow-y: ${(props) => props.isMobile && 'scroll'};
`;

const StyledRadioGroup = styled(Radio.Group)`
  width: 100%;
  margin-bottom: 16px;
`;

const StyledRadioButton = styled(Radio.Button)`
  width: 50%;
  text-align: center;
`;

const StyledRow = styled.div`
  display: flex;
  flex-direction: ${(props) => (props.isMobile ? 'column' : 'row')};
  align-items: center;
  justify-content: space-between;
  margin-top: ${(props) => (props.isMobile ? '0px' : '16px')};
`;

const DELIMETER = '__';

const initialState = {
  unitFormType: UNIT_ASSIGN_FORM,
  site: undefined,
  property: undefined,
  leaseStartDate: undefined,
  selectedRows: [],
  selectedRowKeys: [],
};

function reducer(state, [action, payload]) {
  switch (action) {
    case 'UPDATE':
      return {
        ...state,
        ...payload,
      };
    default:
      return state;
  }
}

const AssignUnit = ({
  selectedApplicant,
  siteAndProperties,
  loadingSiteAndProperties,
  closeDrawer,
  onAdd,
  selectedUnit,
  assignUnitQuery,
}) => {
  const { isMobile } = useContext(ApplicationUIContext);
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({
    order: 'ascend',
    field: 'availability_date_to_display',
    columnKey: 'availability_date_to_display',
  });
  const [loadingTable, setLoadingTable] = useState(false);
  const [availableSites, setAvailableSites] = useState([]);
  const [availableProperties, setAvailableProperties] = useState([]);
  const [availableUnits, setAvailableUnits] = useState([]);

  const [
    { unitFormType, site, property, leaseStartDate, selectedRows, selectedRowKeys },
    dispatch,
  ] = useReducer(reducer, initialState);

  const unitTypeFilters = useMemo(() => {
    const unit_types = availableUnits.map((unit) => ({
      text: unit.unit_type,
      value: unit.unit_type,
    }));
    return [...new Map(unit_types.map((obj) => [JSON.stringify(obj), obj])).values()];
  }, [availableUnits]);

  const propertyNameFilters = useMemo(() => {
    const property_names = availableUnits.map((unit) => ({
      text: unit.property_name,
      value: unit.property_name,
    }));
    return [...new Map(property_names.map((obj) => [JSON.stringify(obj), obj])).values()];
  }, [availableUnits]);

  const loadAvailableUnits = useCallback(
    ({ siteId, propertyId, leaseStartDate, unitFormType, onEdit = false }) => {
      setLoadingTable(true);
      setAvailableUnits([]);
      if (onEdit) {
        siteId = assignUnitQuery.site;
        propertyId = assignUnitQuery.property;
        unitFormType = assignUnitQuery.unit_form_type;
      }

      getAvailableUnits({ siteId, propertyId, unitFormType, leaseStartDate })
        .done((response) => {
          setLoadingTable(false);
          const value = response[Object.keys(response)[0]];
          setAvailableUnits(value);
          if (onEdit) {
            availablePropertiesForSite(assignUnitQuery.site);
            dispatch([
              'UPDATE',
              {
                unitFormType: assignUnitQuery.unit_form_type,
                site: assignUnitQuery.site,
                property: assignUnitQuery.property,
                selectedRows: [selectedUnit],
                selectedRowKeys: [selectedUnit.key],
              },
            ]);
          }
        })
        .catch((error) => {
          setLoadingTable(false);
          showFailure(error);
        });
    },
    [showFailure]
  );

  useEffect(() => {
    if (loadingSiteAndProperties) return;
    const availableSites = siteAndProperties.map((site) => ({
      id: site.key.split(DELIMETER)[1],
      label: site.name,
    }));

    setAvailableSites(availableSites);
    if (!isEmpty(selectedUnit) && !isEmpty(assignUnitQuery)) {
      loadAvailableUnits({
        siteId: assignUnitQuery.site,
        propertyId: assignUnitQuery.property,
        leaseStartDate: assignUnitQuery.lease_start_date,
        unitFormType: assignUnitQuery.unit_form_type,
        onEdit: true,
      });
    }
  }, [siteAndProperties, loadingSiteAndProperties]);

  let UnitAssignTableColumns = [
    {
      title: getTranslatedString('landlordPortal.applicationManagement.inviteLeadToApply.unit'),
      dataIndex: 'unit',
      key: 'unit',
      sorter: (a, b) => basicSort(a.unit, b.unit),
      // Default is sorting by unit number ascending
      sortOrder: sortedInfo ? sortedInfo?.columnKey === 'unit' && sortedInfo?.order : 'ascend',
    },
    {
      title: getTranslatedString('landlordPortal.applicationManagement.inviteLeadToApply.unitType'),
      dataIndex: 'unit_type',
      key: 'unit_type',
      filters: unitTypeFilters,
      filteredValue: filteredInfo.unit_type || null,
      onFilter: (value, record) => record.unit_type.indexOf(value) === 0,
      sorter: (a, b) => alphabeticalColumnSort(a.unit_type, b.unit_type),
      sortOrder: sortedInfo.field === 'unit_type' && sortedInfo.order,
    },
    {
      title: getTranslatedString(
        'landlordPortal.applicationManagement.inviteLeadToApply.marketRent'
      ),
      dataIndex: 'market_rent',
      key: 'market_rent',
      sorter: (a, b) => currencySort(a.market_rent, b.market_rent),
      sortOrder: sortedInfo.field === 'market_rent' && sortedInfo.order,
    },
    {
      title: getTranslatedString(
        'landlordPortal.applicationManagement.inviteLeadToApply.availability'
      ),
      dataIndex: 'availability_date_to_display',
      key: 'availability_date_to_display',
      render(_, { availability_date_to_display }) {
        return availability_date_to_display
          ? moment(availability_date_to_display).locale(getCurrentLocale()).format('MMMM DD, YYYY')
          : getTranslatedString(
              'landlordPortal.applicationManagement.inviteLeadToApply.availableNow'
            );
      },
      sorter: (a, b) => dateSort(a.availability_date_to_display, b.availability_date_to_display),
      sortOrder: sortedInfo.field === 'availability_date_to_display' && sortedInfo.order,
    },
    {
      title: getTranslatedString('landlordPortal.applicationManagement.inviteLeadToApply.property'),
      dataIndex: 'property_name',
      key: 'property_name',
      filters: propertyNameFilters,
      filteredValue: filteredInfo.property_name || null,
      onFilter: (value, record) => record.property_name.indexOf(value) === 0,
      sorter: (a, b) => alphabeticalColumnSort(a.property_name, b.property_name),
      sortOrder: sortedInfo.field === 'property_name' && sortedInfo.order,
    },
    {
      title: getTranslatedString(
        'landlordPortal.applicationManagement.inviteLeadToApply.applications'
      ),
      dataIndex: 'applications_count',
      key: 'applications_count',
      sorter: (a, b) => basicSort(a.applications_count, b.applications_count),
      sortOrder: sortedInfo.field === 'applications_count' && sortedInfo.order,
    },
  ];

  if (hostIsCapreit()) {
    const rentableDateColumnObj = {
      title: getTranslatedString(
        'landlordPortal.applicationManagement.inviteLeadToApply.availability'
      ),
      dataIndex: 'rentable_date',
      key: 'rentable_date',
      render(_, { rentable_date }) {
        return rentable_date
          ? moment(rentable_date).locale(getCurrentLocale()).format('MMMM DD, YYYY')
          : getTranslatedString(
              'landlordPortal.applicationManagement.inviteLeadToApply.availableNow'
            );
      },
      sorter: (a, b) => dateSort(a.rentable_date, b.rentable_date),
      sortOrder: sortedInfo.field === 'rentable_date' && sortedInfo.order,
    };

    UnitAssignTableColumns.splice(4, 0, rentableDateColumnObj);
  }

  const WaitUnitAssignTableColumns = [
    {
      title: getTranslatedString('landlordPortal.applicationManagement.inviteLeadToApply.unitType'),
      dataIndex: 'unit_type',
      key: 'unit_type',
    },
    {
      title: getTranslatedString('landlordPortal.applicationManagement.inviteLeadToApply.property'),
      dataIndex: 'property_names',
      render: (text, record) => text.join(', '),
      ellipsis: true,
    },
  ];

  const availablePropertiesForSite = (siteId) => {
    const selectedSite = siteAndProperties.find((site) => site.key.split(DELIMETER)[1] === siteId);
    let properties = [
      {
        id: 'all',
        label: getTranslatedString('landlordPortal.applicationManagement.inviteLeadToApply.all'),
      },
    ];
    const availableProperties = properties.concat(
      selectedSite.properties.map((property) => ({
        id: property.key.split(DELIMETER)[1],
        label: property.name,
      }))
    );
    setAvailableProperties(availableProperties);
  };

  const onSiteChange = (value) => {
    dispatch([
      'UPDATE',
      {
        site: value,
        property: 'all',
        selectedRows: [],
        selectedRowKeys: [],
      },
    ]);
    availablePropertiesForSite(value);
    loadAvailableUnits({
      siteId: value,
      propertyId: 'all',
      leaseStartDate,
      unitFormType,
    });
  };

  const onPropertyChange = (value) => {
    dispatch([
      'UPDATE',
      {
        property: value,
        selectedRows: [],
        selectedRowKeys: [],
      },
    ]);
    loadAvailableUnits({
      siteId: site,
      propertyId: value,
      leaseStartDate,
      unitFormType,
    });
  };

  const onLeaseStartDateChange = (value) => {
    dispatch(['UPDATE', { leaseStartDate: value, selectedRows: [], selectedRowKeys: [] }]);
    loadAvailableUnits({
      siteId: site,
      propertyId: property,
      leaseStartDate: value,
      unitFormType,
    });
  };

  const onRowSelect = (selectedRowKeys, selectedRows) => {
    dispatch([
      'UPDATE',
      {
        selectedRows: selectedRows,
        selectedRowKeys: selectedRowKeys,
      },
    ]);
  };

  const onFormTypeChange = (e) => {
    // need to reset selected row on form type change
    // or if same row with same id on different form type exists, it will get pre-selected
    dispatch([
      'UPDATE',
      {
        unitFormType: e.target.value,
        selectedRows: [],
        selectedRowKeys: [],
      },
    ]);
    if (site || property) {
      loadAvailableUnits({
        siteId: site,
        propertyId: property,
        leaseStartDate,
        unitFormType: e.target.value,
      });
    }
  };

  const handleTableChange = (_, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  const rowSelection = {
    selectedRowKeys: selectedRowKeys,
    onChange: onRowSelect,
    fixed: true,
  };

  const tableColumns =
    unitFormType === UNIT_ASSIGN_FORM ? UnitAssignTableColumns : WaitUnitAssignTableColumns;

  return (
    <StyledContainer isMobile={isMobile}>
      {featureEnabled('ui.applications.assign_unit_later') && (
        <StyledRadioGroup value={unitFormType} onChange={onFormTypeChange}>
          <StyledRadioButton value={UNIT_ASSIGN_FORM}>
            {getTranslatedString(
              'landlordPortal.applicationManagement.inviteLeadToApply.assignUnitNow'
            )}
          </StyledRadioButton>

          <StyledRadioButton value={WAIT_UNIT_FORM}>
            {getTranslatedString(
              'landlordPortal.applicationManagement.inviteLeadToApply.assignUnitLater'
            )}
          </StyledRadioButton>
        </StyledRadioGroup>
      )}

      {selectedApplicant && selectedApplicant.lead_card && (
        <LeadPreferencesAlertInfo
          applicantName={selectedApplicant.full_name}
          property={selectedApplicant.lead_card.property_name}
          unitType={selectedApplicant.lead_card.desired_unit_type}
          moveInDate={selectedApplicant.lead_card.target_move_in_date}
          maxBudget={selectedApplicant.lead_card.max_budget_cents}
          minBudget={selectedApplicant.lead_card.min_budget_cents}
        />
      )}

      <StyledRow isMobile={isMobile}>
        <SiteField
          isMobile={isMobile}
          value={site}
          onChange={onSiteChange}
          loading={loadingSiteAndProperties}
          availableSites={availableSites}
        />

        <PropertyField
          isMobile={isMobile}
          value={property}
          onChange={onPropertyChange}
          loading={loadingSiteAndProperties}
          availableProperties={availableProperties}
        />
      </StyledRow>

      <StyledRow isMobile={isMobile}>
        <LeaseStartDateField
          isMobile={isMobile}
          value={leaseStartDate}
          onChange={onLeaseStartDateChange}
        />
        <GroupField isMobile={isMobile} value="" onChange={onLeaseStartDateChange} />
      </StyledRow>

      <AssignUnitTable
        isMobile={isMobile}
        dataSource={availableUnits}
        columns={tableColumns}
        hideActionAndSearch={true}
        onChange={handleTableChange}
        rowSelection={{
          type: 'radio',
          ...rowSelection,
        }}
        pagination={{ pageSize: 10 }}
        loading={loadingTable}
      />

      <ActionFooter
        isMobile={isMobile}
        disabled={isEmpty(selectedRows)}
        primaryLabel={getTranslatedString('add')}
        primaryAction={() => {
          // selectedRows for radio button table will only return an array with 1 (selected) element
          onAdd(selectedRows[0], unitFormType, site, property);
        }}
        secondaryLabel={getTranslatedString('cancel')}
        secondaryAction={closeDrawer}
      />
    </StyledContainer>
  );
};

AssignUnit.propTypes = {
  selectedApplicant: PropTypes.shape({
    name: PropTypes.string,
    lead_card: PropTypes.shape({
      property: PropTypes.string,
      unit_type: PropTypes.string,
      move_in_date: PropTypes.string,
      max_budget_cents: PropTypes.number,
      min_budget_cents: PropTypes.number,
    }),
  }),
  siteAndProperties: PropTypes.array,
  loadingSiteAndProperties: PropTypes.bool,
  closeDrawer: PropTypes.func,
  onAdd: PropTypes.func,
  selectedUnit: PropTypes.object,
  assignUnitQuery: PropTypes.object,
};

AssignUnit.defaultProps = {
  selectedApplicant: null,
};

export default connect(null, { showFailure })(AssignUnit);
